import { useEffect, useId, useRef, useState } from 'react';
import { CloseIcon, QrIcon } from './icons';
import { codeFromQr } from '../lib/code';

interface Props { brandName: string; browserHref: string; browserLabel: string; showBrowserHandoff: boolean; onCode: (code: string) => void; onClose: () => void }

export function QrScanner({ brandName, browserHref, browserLabel, showBrowserHandoff, onCode, onClose }: Props) {
  const rawId = useId();
  const elementId = `qr-reader-${rawId.replace(/:/g, '')}`;
  const closeButton = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const background = Array.from(document.querySelectorAll<HTMLElement>('.brand-header, .journey-strip, .portal-content, .portal-shell > footer'));
    background.forEach((element) => { element.inert = true; element.setAttribute('aria-hidden', 'true'); });
    closeButton.current?.focus();
    return () => {
      background.forEach((element) => { element.inert = false; element.removeAttribute('aria-hidden'); });
      previous?.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') return onClose();
      if (event.key !== 'Tab' || !sheet.current) return;
      const focusable = Array.from(sheet.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let stopped = false;
    let scanner: import('html5-qrcode').Html5Qrcode | undefined;
    void import('html5-qrcode').then(async ({ Html5Qrcode }) => {
      if (stopped) return;
      scanner = new Html5Qrcode(elementId);
      try {
        await scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 230, height: 230 } }, (raw) => {
          const code = codeFromQr(raw);
          if (code) onCode(code); else setError('Este QR no corresponde a un acceso de este sistema. Prueba con la etiqueta asignada.');
        }, () => undefined);
      } catch {
        setError('No pudimos abrir la cámara. Revisa el permiso del navegador o ingresa el código manualmente.');
      }
    });
    return () => { stopped = true; if (scanner?.isScanning) void scanner.stop().catch(() => undefined); };
  }, [elementId, onCode]);

  return <div className="scanner-modal" role="dialog" aria-modal="true" aria-labelledby="scanner-title">
    <div ref={sheet} className="scanner-sheet">
      <div className="sheet-handle" aria-hidden="true" />
      <div className="sheet-head">
        <div className="sheet-title-icon"><QrIcon /></div>
        <div><strong id="scanner-title">Escanea tu QR de acceso</strong><span>{brandName} reconocerá tu ubicación automáticamente.</span></div>
        <button ref={closeButton} className="icon-button" onClick={onClose} aria-label="Cerrar escáner"><CloseIcon /></button>
      </div>
      <div className="camera-frame"><div id={elementId} className="qr-reader" /><span className="corner top-left" /><span className="corner top-right" /><span className="corner bottom-left" /><span className="corner bottom-right" /></div>
      <p className="camera-hint">Centra el código completo dentro del recuadro.</p>
      {error && <div className="camera-error" role="alert"><p>{error}</p>{showBrowserHandoff && <a href={browserHref} target={browserHref.startsWith('intent:') ? undefined : '_blank'} rel="noopener noreferrer">{browserLabel}</a>}</div>}
      <button className="secondary-button" onClick={onClose}>Ingresar código manualmente</button>
    </div>
  </div>;
}
